<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useSettingsOverlay } from '#layers/global-settings/composables/use-settings-overlay';
import { panelFor } from '#layers/global-settings/constants/settings-overlay-panels';
import { WIDE_MODAL_CONTENT_CLASS } from '#layers/ui/public';

const { isOpen, activeSection, close } = useSettingsOverlay();

// Hold the last opened section so the panel keeps rendering while the modal animates out.
// Clearing it as soon as `activeSection` goes null on close would flash the account/profile
// fallback for the duration of the exit transition.
const displayedSection = ref(activeSection.value);

watch(activeSection, (section) => {
	if (section !== null) displayedSection.value = section;
});

const panel = computed(() => panelFor(displayedSection.value));
</script>

<template>
	<DModal
		:open="isOpen"
		:title="$t('overlay.title')"
		:ui="{ content: WIDE_MODAL_CONTENT_CLASS, body: 'p-0 sm:p-0' }"
		@update:open="(value: boolean) => {
			if (!value) close();
		}"
	>
		<template #content>
			<div class="relative flex h-full min-h-0">
				<div class="flex w-56 shrink-0 flex-col border-e bg-dimmed">
					<SettingsOverlaySidebar />
				</div>
				<div class="flex min-h-0 flex-1 flex-col overflow-y-auto">
					<Suspense>
						<component :is="panel" />
						<template #fallback>
							<div class="p-8">
								<DSkeleton class="h-8 w-48" />
							</div>
						</template>
					</Suspense>
				</div>
				<DButton
					icon="lucide:x"
					color="neutral"
					variant="ghost"
					class="absolute inset-e-3 top-3 z-10"
					:aria-label="$t('common.close')"
					@click="close()"
				/>
			</div>
		</template>
	</DModal>
</template>
